import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { useAuth } from "@/app/AuthContext";

import {
  fetchOrganizations,
  fetchOrganization,
  fetchDriverGroups,
  createDriverGroup,
  deleteDriverGroup,
  fetchDriverGroupMembers,
  addDriverGroupMember,
  removeDriverGroupMember,
  fetchChargerGroups,
  createChargerGroup,
  deleteChargerGroup,
  fetchChargerGroupMembers,
  addChargerGroupMember,
  removeChargerGroupMember,
  fetchAccessPolicies,
  createAccessPolicy,
  updateAccessPolicy,
  deleteAccessPolicy,
  fetchAccessRules,
  createAccessRule,
  updateAccessRule,
  deleteAccessRule,
  fetchRulePolicies,
  fetchUnits,
  Organization,
  DriverGroup,
  ChargerGroup,
  AccessPolicy,
  AccessRule,
} from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import {
  SearchableSelect,
} from "@/components/ui/searchable-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Search, X } from "@/components/icons";
import {
  autoDriverGroupName,
  autoGroupName,
  isAutoGroup,
  isReservedManualGroupName,
  RESERVED_GROUP_NAME_MESSAGE,
} from "./accessControlGroups";

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Renders a rule/policy group reference, flagging rule-managed auto groups. */
function GroupRefCell({ name }: { name: string | undefined }) {
  if (!name) return <span className="opacity-40">?</span>;
  if (isAutoGroup(name)) {
    return (
      <span
        className="inline-flex items-center gap-1.5"
        title="Auto-managed by this rule. It is hidden from the group panels — edit it through the rule, not as a standalone group."
      >
        <span>{name}</span>
        <Badge variant="info" className="badge-sm">auto</Badge>
      </span>
    );
  }
  return <span>{name}</span>;
}

/** Count how many policies reference a group */
function policyCountForGroup(policies: AccessPolicy[], groupId: number, field: "driver_group_id" | "charger_group_id") {
  return policies.filter((p) => p[field] === groupId).length;
}

const RULE_TYPE_LABELS: Record<string, { label: string; description: string; variant: "success" | "error" | "info" }> = {
  allow_only: { label: "Allow only", description: "Only these users can charge. All others are blocked. Requires sign-in.", variant: "info" },
  block: { label: "Everyone except", description: "All registered users can charge except these. Requires sign-in.", variant: "error" },
};

/* ------------------------------------------------------------------ */
/*  ComboInput — text input with autocomplete suggestions              */
/* ------------------------------------------------------------------ */

function UnitComboInput({ value, onChange, placeholder, orgUnitIds }: {
  value: string; onChange: (v: string) => void; placeholder?: string; orgUnitIds?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState(value);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(t);
  }, [value]);

  const unitsQ = useQuery({
    queryKey: ["units-search", debounced],
    queryFn: () => fetchUnits({ start: 0, end: 20, search: debounced || undefined }),
    enabled: open && debounced.length > 0,
  });
  const orgUnitSet = useMemo(() => orgUnitIds ? new Set(orgUnitIds) : null, [orgUnitIds]);
  const results = useMemo(() => {
    const units = unitsQ.data?.units ?? [];
    return orgUnitSet ? units.filter((u) => orgUnitSet.has(u.unit_id)) : units;
  }, [unitsQ.data, orgUnitSet]);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative mt-2" ref={ref}>
      <Input
        className="font-mono"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg max-h-48 overflow-y-auto">
          {results.map((u) => (
            <button
              key={u.unit_id}
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-100 flex items-center gap-2"
              onClick={() => { onChange(u.unit_id); setOpen(false); }}
            >
              <span className="font-mono">{u.unit_id}</span>
              {u.name && <span className="text-xs opacity-50">{u.name}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Org selector                                                       */
/* ------------------------------------------------------------------ */

function OrgSelector({ orgs, selected, onSelect }: {
  orgs: Organization[]; selected: number | null; onSelect: (id: number) => void;
}) {
  if (orgs.length === 0) return <div className="text-sm opacity-50">No organizations found. Create one first.</div>;
  return (
    <SearchableSelect
      className="w-64"
      options={orgs.map((o) => ({ value: String(o.id), label: o.name }))}
      value={selected != null ? String(selected) : null}
      onValueChange={(v) => onSelect(Number(v))}
      placeholder="Select organization..."
      searchPlaceholder="Search organizations..."
      emptyText="No organizations match."
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Add Member Modals                                                  */
/* ------------------------------------------------------------------ */

function AddUserMemberModal({ open, onOpenChange, orgId, groupId, existingIds, onAdded }: {
  open: boolean; onOpenChange: (v: boolean) => void; orgId: number; groupId: number;
  existingIds: Set<number>; onAdded: () => void;
}) {
  const [search, setSearch] = useState("");
  const orgQ = useQuery({ queryKey: ["organization", orgId], queryFn: () => fetchOrganization(orgId), enabled: open });
  const addM = useMutation({
    mutationFn: (userId: number) => addDriverGroupMember(orgId, groupId, userId),
    onSuccess: () => { toast.success("User added"); onAdded(); onOpenChange(false); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });
  const available = useMemo(
    () => (orgQ.data?.organization?.users ?? []).filter((u) => !existingIds.has(u.id) && (!search || u.username.toLowerCase().includes(search.toLowerCase()))),
    [orgQ.data, existingIds, search],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Add User to Group</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          <Input className="pl-9" placeholder="Search org users..." value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {orgQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">Loading...</div>}
          {available.length === 0 && !orgQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">No matching users</div>}
          {available.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50">
              <span className="font-medium text-sm">{u.username}</span>
              <Button size="sm" variant="outline" onClick={() => addM.mutate(u.id)} disabled={addM.isPending}>Add</Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddUnitMemberModal({ open, onOpenChange, orgId, groupId, existingIds, onAdded, orgUnitIds }: {
  open: boolean; onOpenChange: (v: boolean) => void; orgId: number; groupId: number;
  existingIds: Set<string>; onAdded: () => void; orgUnitIds?: string[];
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);
  const unitsQ = useQuery({
    queryKey: ["units-search", debouncedSearch],
    queryFn: () => fetchUnits({ start: 0, end: 50, search: debouncedSearch || undefined }),
    enabled: open,
  });
  const addM = useMutation({
    mutationFn: (unitId: string) => addChargerGroupMember(orgId, groupId, unitId),
    onSuccess: () => { toast.success("Unit added"); onAdded(); onOpenChange(false); setSearch(""); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });
  const orgUnitSet = useMemo(() => orgUnitIds ? new Set(orgUnitIds) : null, [orgUnitIds]);
  const available = useMemo(() => {
    let units = unitsQ.data?.units ?? [];
    if (orgUnitSet) units = units.filter((u) => orgUnitSet.has(u.unit_id));
    return units.filter((u) => !existingIds.has(u.unit_id));
  }, [unitsQ.data, existingIds, orgUnitSet]);
  const canAddCustom = search.trim() && !existingIds.has(search.trim()) && !available.some((u) => u.unit_id === search.trim());
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Add Unit to Group</DialogTitle></DialogHeader>
        <form className="relative" onSubmit={(e) => { e.preventDefault(); if (search.trim() && !existingIds.has(search.trim())) addM.mutate(search.trim()); }}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          <Input className="pl-9 font-mono" placeholder="Search by ID or name..." value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
        </form>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {canAddCustom && (
            <div className="flex items-center justify-between py-2 px-2 rounded bg-blue-50">
              <span className="font-mono text-sm">{search.trim()}</span>
              <Button size="sm" variant="outline" onClick={() => addM.mutate(search.trim())} disabled={addM.isPending}>Add "{search.trim()}"</Button>
            </div>
          )}
          {unitsQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">Loading...</div>}
          {available.length === 0 && !canAddCustom && !unitsQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">No matching units. Type a unit ID to add it directly.</div>}
          {available.slice(0, 50).map((u) => (
            <div key={u.unit_id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{u.unit_id}</span>
                {u.name && <span className="text-xs opacity-50">{u.name}</span>}
              </div>
              <Button size="sm" variant="outline" onClick={() => addM.mutate(u.unit_id)} disabled={addM.isPending}>Add</Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Rule Form Modal (create + edit)                                    */
/* ------------------------------------------------------------------ */

function RuleFormModal({
  open, onOpenChange, orgId, driverGroups, chargerGroups, orgUsers, onSaved,
  editRule, orgUnitIds,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; orgId: number;
  driverGroups: DriverGroup[]; chargerGroups: ChargerGroup[];
  orgUsers: { id: number; username: string }[];
  onSaved: () => void;
  editRule?: AccessRule | null;
  orgUnitIds?: string[];
}) {
  const isEdit = !!editRule;
  const [name, setName] = useState("");
  const [ruleType, setRuleType] = useState<"allow_only" | "block" | "allow">("allow_only");
  const [driverTarget, setDriverTarget] = useState<"group" | "single">("group");
  const [driverGroupId, setDriverGroupId] = useState("");
  const [singleUserId, setSingleUserId] = useState("");
  const [chargerTarget, setChargerTarget] = useState<"group" | "single">("group");
  const [chargerGroupId, setChargerGroupId] = useState("");
  const [singleUnitId, setSingleUnitId] = useState("");

  // Load managed policies for edit dialog (read-only)
  const rulePoliciesQ = useQuery({
    queryKey: ["rule-policies", orgId, editRule?.id],
    queryFn: () => fetchRulePolicies(orgId, editRule!.id),
    enabled: isEdit && open,
  });

  useEffect(() => {
    if (!open) return;
    if (editRule) {
      setName(editRule.name);
      setRuleType(editRule.rule_type);
      // Detect if current groups are auto-managed
      const dg = driverGroups.find((g) => g.id === editRule.driver_group_id);
      const cg = chargerGroups.find((g) => g.id === editRule.charger_group_id);
      const driverIsAuto = dg && isAutoGroup(dg.name);
      const chargerIsAuto = cg && isAutoGroup(cg.name);
      setDriverTarget(driverIsAuto ? "single" : "group");
      setDriverGroupId(String(editRule.driver_group_id));
      setSingleUserId("");
      setChargerTarget(chargerIsAuto ? "single" : "group");
      setChargerGroupId(String(editRule.charger_group_id));
      setSingleUnitId("");
    } else {
      setName(""); setRuleType("allow_only");
      setDriverTarget("group"); setDriverGroupId(""); setSingleUserId("");
      setChargerTarget("group"); setChargerGroupId(""); setSingleUnitId("");
    }
  }, [open, editRule]);

  const submitM = useMutation({
    mutationFn: async () => {
      // Resolve driver group
      let targetDriverGroupId: number;
      if (driverTarget === "single") {
        // Reuse existing auto-group or create a new one; add the selected user
        const groupName = autoDriverGroupName(name.trim());
        const existing = driverGroups.find((g) => g.name === groupName);
        if (existing) {
          targetDriverGroupId = existing.id;
        } else if (isEdit) {
          // In edit mode, reuse the rule's current auto-group
          targetDriverGroupId = editRule!.driver_group_id;
        } else {
          const res = await createDriverGroup(orgId, groupName);
          targetDriverGroupId = res.driver_group.id;
        }
        if (singleUserId) {
          await addDriverGroupMember(orgId, targetDriverGroupId, Number(singleUserId)).catch(() => {});
        }
      } else {
        targetDriverGroupId = Number(driverGroupId);
      }

      // Resolve charger group
      let targetChargerGroupId: number;
      if (chargerTarget === "single") {
        // Reuse existing auto-group or create a new one; add the selected charger
        const groupName = autoGroupName(name.trim());
        const existing = chargerGroups.find((g) => g.name === groupName);
        if (existing) {
          targetChargerGroupId = existing.id;
        } else if (isEdit) {
          // In edit mode, reuse the rule's current auto-group
          targetChargerGroupId = editRule!.charger_group_id;
        } else {
          const res = await createChargerGroup(orgId, groupName);
          targetChargerGroupId = res.charger_group.id;
          await addChargerGroupMember(orgId, targetChargerGroupId, singleUnitId.trim());
        }
        if (singleUnitId.trim()) {
          await addChargerGroupMember(orgId, targetChargerGroupId, singleUnitId.trim()).catch(() => {});
        }
      } else {
        targetChargerGroupId = Number(chargerGroupId);
      }

      if (isEdit) {
        return updateAccessRule(orgId, editRule!.id, {
          name: name.trim(),
          driver_group_id: targetDriverGroupId,
          charger_group_id: targetChargerGroupId,
        });
      }
      return createAccessRule(orgId, {
        name: name.trim(),
        rule_type: ruleType,
        driver_group_id: targetDriverGroupId,
        charger_group_id: targetChargerGroupId,
      });
    },
    onSuccess: () => {
      toast.success(isEdit ? "Rule updated" : "Rule created");
      onSaved();
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const driverValid = driverTarget === "group" ? !!driverGroupId : (isEdit || !!singleUserId);
  const chargerValid = chargerTarget === "group" ? !!chargerGroupId : (isEdit || !!singleUnitId.trim());
  const valid = name.trim() && driverValid && chargerValid;

  const managedPolicies = rulePoliciesQ.data?.policies ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? "Edit Rule" : "New Access Rule"}
            <Badge variant={RULE_TYPE_LABELS[ruleType]?.variant ?? "neutral"} className="badge-sm uppercase">
              {RULE_TYPE_LABELS[ruleType]?.label ?? ruleType}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submitM.mutate(); }}>
          {/* Rule type selector — only for create */}
          {!isEdit && (
            <div>
              <Label>What do you want to do?</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {(["allow_only", "block"] as const).map((rt) => {
                  const meta = RULE_TYPE_LABELS[rt];
                  return (
                    <button
                      key={rt}
                      type="button"
                      className={`rounded-lg border p-3 text-left text-xs transition-colors ${
                        ruleType === rt
                          ? "border-zinc-900 bg-zinc-50 ring-1 ring-zinc-900"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                      onClick={() => setRuleType(rt)}
                    >
                      <div className="font-semibold">{meta.label}</div>
                      <div className="mt-1 opacity-60">{meta.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label>Rule Name *</Label>
            <Input className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)}
              placeholder={ruleType === "allow_only" ? "e.g. Only VIP on Reserved Chargers" : "e.g. Everyone except Contractors on Building A"} />
          </div>

          {/* Who — driver group or single user */}
          <div>
            <Label>{ruleType === "allow_only" ? "Who can charge (who) *" : "Who to block (who) *"}</Label>
            <div className="flex gap-2 mt-1.5">
              <Button type="button" size="sm" variant={driverTarget === "group" ? "default" : "outline"} onClick={() => setDriverTarget("group")}>User Group</Button>
              <Button type="button" size="sm" variant={driverTarget === "single" ? "default" : "outline"} onClick={() => setDriverTarget("single")}>{isEdit ? "Add User" : "Single User"}</Button>
            </div>
            {driverTarget === "group" ? (
              <SearchableSelect
                className="mt-2 w-full"
                value={driverGroupId || null}
                onValueChange={setDriverGroupId}
                placeholder="Select driver group..."
                searchPlaceholder="Search driver groups..."
                emptyText="No driver groups match."
                options={driverGroups
                  .filter((g) => !(ruleType === "allow_only" && g.is_builtin) && !isAutoGroup(g.name))
                  .map((g) => ({
                    value: String(g.id),
                    label: `${g.name}${g.is_builtin ? " (built-in)" : ""} (${g.member_count} members)`,
                  }))}
              />
            ) : (
              <SearchableSelect
                className="mt-2 w-full"
                value={singleUserId || null}
                onValueChange={setSingleUserId}
                placeholder={isEdit ? "Add a user to this rule..." : "Select user..."}
                searchPlaceholder="Search users..."
                emptyText="No users match."
                options={orgUsers.map((u) => ({ value: String(u.id), label: u.username }))}
              />
            )}
          </div>

          {/* Where — charger group or single charger */}
          <div>
            <Label>Charger Target (where) *</Label>
            <div className="flex gap-2 mt-1.5">
              <Button type="button" size="sm" variant={chargerTarget === "group" ? "default" : "outline"} onClick={() => setChargerTarget("group")}>Charger Group</Button>
              <Button type="button" size="sm" variant={chargerTarget === "single" ? "default" : "outline"} onClick={() => setChargerTarget("single")}>{isEdit ? "Add Charger" : "Single Charger"}</Button>
            </div>
            {chargerTarget === "group" ? (
              <SearchableSelect
                className="mt-2 w-full"
                value={chargerGroupId || null}
                onValueChange={setChargerGroupId}
                placeholder="Select charger group..."
                searchPlaceholder="Search charger groups..."
                emptyText="No charger groups match."
                options={chargerGroups
                  .filter((g) => !isAutoGroup(g.name))
                  .map((g) => ({ value: String(g.id), label: `${g.name} (${g.member_count} units)` }))}
              />
            ) : (
              <UnitComboInput value={singleUnitId} onChange={setSingleUnitId}
                placeholder="Type or search charger ID..." orgUnitIds={orgUnitIds} />
            )}
          </div>

          {/* Read-only managed policies (edit only) */}
          {isEdit && managedPolicies.length > 0 && (
            <details className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
              <summary className="cursor-pointer text-xs font-medium text-zinc-500">
                Under the hood ({managedPolicies.length} auto-managed policies)
              </summary>
              <div className="mt-2 space-y-1">
                {managedPolicies.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 text-xs text-zinc-600">
                    <Badge variant={p.rule_type === "deny" ? "error" : "success"} className="badge-sm uppercase">{p.rule_type}</Badge>
                    <span className="font-mono">priority {p.priority}</span>
                    <span className="truncate">{p.name}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          <DialogFooter>
            <Button type="submit" disabled={submitM.isPending || !valid}>
              {submitM.isPending ? "Saving..." : isEdit ? "Update Rule" : "Create Rule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Legacy Policy Form Modal (for advanced/manual policies)            */
/* ------------------------------------------------------------------ */

function PolicyFormModal({
  open, onOpenChange, orgId, driverGroups, chargerGroups, onSaved,
  editPolicy, orgUnitIds,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; orgId: number;
  driverGroups: DriverGroup[]; chargerGroups: ChargerGroup[];
  onSaved: () => void;
  editPolicy?: AccessPolicy | null;
  orgUnitIds?: string[];
}) {
  const isEdit = !!editPolicy;
  const [name, setName] = useState("");
  const [ruleType, setRuleType] = useState<"grant" | "deny">("grant");
  const [driverGroupId, setDriverGroupId] = useState("");
  const [chargerTarget, setChargerTarget] = useState<"group" | "single">("group");
  const [chargerGroupId, setChargerGroupId] = useState("");
  const [singleUnitId, setSingleUnitId] = useState("");
  const [priority, setPriority] = useState("100");

  useEffect(() => {
    if (!open) return;
    if (editPolicy) {
      setName(editPolicy.name);
      setRuleType(editPolicy.rule_type);
      setDriverGroupId(String(editPolicy.driver_group_id));
      setChargerGroupId(String(editPolicy.charger_group_id));
      setChargerTarget("group");
      setSingleUnitId("");
      setPriority(String(editPolicy.priority));
    } else {
      setName(""); setRuleType("grant"); setDriverGroupId("");
      setChargerTarget("group"); setChargerGroupId(""); setSingleUnitId("");
      setPriority("100");
    }
  }, [open, editPolicy]);

  const submitM = useMutation({
    mutationFn: async () => {
      let targetGroupId: number;

      if (chargerTarget === "single" && !isEdit) {
        const groupName = autoGroupName(name.trim());
        const existing = chargerGroups.find((g) => g.name === groupName);
        if (existing) {
          targetGroupId = existing.id;
          await addChargerGroupMember(orgId, targetGroupId, singleUnitId).catch(() => {});
        } else {
          const res = await createChargerGroup(orgId, groupName);
          targetGroupId = res.charger_group.id;
          await addChargerGroupMember(orgId, targetGroupId, singleUnitId);
        }
      } else {
        targetGroupId = Number(chargerGroupId);
      }

      if (isEdit) {
        return updateAccessPolicy(orgId, editPolicy!.id, {
          name: name.trim(),
          rule_type: ruleType,
          driver_group_id: Number(driverGroupId),
          charger_group_id: targetGroupId,
          priority: Number(priority),
        });
      }
      return createAccessPolicy(orgId, {
        name: name.trim(),
        rule_type: ruleType,
        driver_group_id: Number(driverGroupId),
        charger_group_id: targetGroupId,
        priority: Number(priority),
      });
    },
    onSuccess: () => {
      toast.success(isEdit ? "Policy updated" : "Policy created");
      onSaved();
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const valid = name.trim() && driverGroupId && (chargerTarget === "group" ? !!chargerGroupId : !!singleUnitId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? "Edit Policy" : "New Advanced Policy"}
            {ruleType === "deny"
              ? <Badge variant="error" className="badge-sm uppercase">deny</Badge>
              : <Badge variant="success" className="badge-sm uppercase">grant</Badge>}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submitM.mutate(); }}>
          <div>
            <Label>Policy Name *</Label>
            <Input className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Block contractors from Building A" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rule Type *</Label>
              <div className="flex gap-2 mt-1.5">
                <Badge
                  className={`cursor-pointer px-3 py-1.5 text-xs ${ruleType === "grant" ? "" : "opacity-30"}`}
                  variant="success"
                  onClick={() => setRuleType("grant")}
                >GRANT (allow)</Badge>
                <Badge
                  className={`cursor-pointer px-3 py-1.5 text-xs ${ruleType === "deny" ? "" : "opacity-30"}`}
                  variant="error"
                  onClick={() => setRuleType("deny")}
                >DENY (block)</Badge>
              </div>
            </div>
            <div>
              <Label>Priority</Label>
              <Input className="mt-1.5" type="number" value={priority} onChange={(e) => setPriority(e.target.value)} min={1} max={999} />
              <div className="text-xs opacity-50 mt-1">Lower = higher priority</div>
            </div>
          </div>
          <div>
            <Label>Driver Group (who) *</Label>
            <SearchableSelect
              className="mt-1.5 w-full"
              value={driverGroupId || null}
              onValueChange={setDriverGroupId}
              placeholder="Select driver group..."
              searchPlaceholder="Search driver groups..."
              emptyText="No driver groups match."
              options={driverGroups.map((g) => ({
                value: String(g.id),
                label: `${g.name}${g.is_builtin ? " (built-in)" : ""} (${g.member_count} members)`,
              }))}
            />
          </div>
          <div>
            <Label>Charger Target (where) *</Label>
            {!isEdit && (
              <div className="flex gap-2 mt-1.5">
                <Button type="button" size="sm" variant={chargerTarget === "group" ? "default" : "outline"} onClick={() => setChargerTarget("group")}>Charger Group</Button>
                <Button type="button" size="sm" variant={chargerTarget === "single" ? "default" : "outline"} onClick={() => setChargerTarget("single")}>Single Charger</Button>
              </div>
            )}
            {chargerTarget === "group" || isEdit ? (
              <SearchableSelect
                className="mt-2 w-full"
                value={chargerGroupId || null}
                onValueChange={setChargerGroupId}
                placeholder="Select charger group..."
                searchPlaceholder="Search charger groups..."
                emptyText="No charger groups match."
                options={chargerGroups.map((g) => ({ value: String(g.id), label: `${g.name} (${g.member_count} units)` }))}
              />
            ) : (
              <UnitComboInput value={singleUnitId} onChange={setSingleUnitId}
                placeholder="Type or search charger ID..." orgUnitIds={orgUnitIds} />
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitM.isPending || !valid}>
              {submitM.isPending ? "Saving..." : isEdit ? "Update Policy" : "Create Policy"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Delete group confirmation dialog                                   */
/* ------------------------------------------------------------------ */

function DeleteGroupDialog({ open, onOpenChange, groupName, policyCount, onConfirm, isPending }: {
  open: boolean; onOpenChange: (v: boolean) => void; groupName: string;
  policyCount: number; onConfirm: () => void; isPending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Group</DialogTitle>
          <DialogDescription>
            {policyCount > 0
              ? `Group "${groupName}" is used in ${policyCount} policy(ies). Deleting it will also delete all associated policies.`
              : `Delete group "${groupName}"?`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {isPending ? "Deleting..." : policyCount > 0 ? `Delete Group + ${policyCount} Policy(ies)` : "Delete Group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Group Card                                                         */
/* ------------------------------------------------------------------ */

function GroupCard({ group, type, orgId, policies, onDeleted, userMap, orgUnitIds }: {
  group: DriverGroup | ChargerGroup; type: "driver" | "charger"; orgId: number;
  policies: AccessPolicy[]; onDeleted: () => void; userMap?: Map<number, string>; orgUnitIds?: string[];
}) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isBuiltin = "is_builtin" in group && group.is_builtin;
  const field = type === "driver" ? "driver_group_id" : "charger_group_id";
  const refCount = policyCountForGroup(policies, group.id, field);

  const driverMembersQ = useQuery({ queryKey: ["driver-group-members", orgId, group.id], queryFn: () => fetchDriverGroupMembers(orgId, group.id), enabled: type === "driver" });
  const chargerMembersQ = useQuery({ queryKey: ["charger-group-members", orgId, group.id], queryFn: () => fetchChargerGroupMembers(orgId, group.id), enabled: type === "charger" });
  const membersQ = type === "driver" ? driverMembersQ : chargerMembersQ;

  const deleteM = useMutation({
    mutationFn: () => type === "driver" ? deleteDriverGroup(orgId, group.id) : deleteChargerGroup(orgId, group.id),
    onSuccess: () => { toast.success("Group deleted"); setDeleteOpen(false); onDeleted(); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const removeMemberM = useMutation({
    mutationFn: (memberId: string | number) =>
      type === "driver" ? removeDriverGroupMember(orgId, group.id, memberId as number) : removeChargerGroupMember(orgId, group.id, memberId as string),
    onSuccess: () => { toast.success("Member removed"); membersQ.refetch(); qc.invalidateQueries({ queryKey: [`${type}-groups`, orgId] }); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const members: (string | number)[] = type === "driver" ? (driverMembersQ.data?.members ?? []) : (chargerMembersQ.data?.members ?? []);
  const refreshMembers = () => { membersQ.refetch(); qc.invalidateQueries({ queryKey: [`${type}-groups`, orgId] }); };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">{group.name}</CardTitle>
            {isBuiltin && <Badge variant="secondary" className="badge-sm">built-in</Badge>}
            {isAutoGroup(group.name) && <Badge variant="info" className="badge-sm">auto</Badge>}
            {refCount > 0 && <Badge variant="neutral" className="badge-sm">{refCount} rule(s)</Badge>}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>+ Add</Button>
            {!isBuiltin && (
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                onClick={() => setDeleteOpen(true)}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {members.length === 0 ? (
          <div className="text-sm opacity-50 italic">No members</div>
        ) : (
          <div className="space-y-1">
            {members.map((m) => (
              <div key={String(m)} className="flex items-center justify-between py-1 px-2 rounded hover:bg-muted/50 text-sm">
                <span className={type === "charger" ? "font-mono" : "font-medium"}>
                  {type === "driver" && userMap ? (userMap.get(m as number) ?? String(m)) : String(m)}
                </span>
                {!isBuiltin && (
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-destructive hover:text-destructive"
                    onClick={() => removeMemberM.mutate(m)}>
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {type === "driver" && addOpen && (
        <AddUserMemberModal open={addOpen} onOpenChange={setAddOpen} orgId={orgId} groupId={group.id}
          existingIds={new Set(members as number[])} onAdded={refreshMembers} />
      )}
      {type === "charger" && addOpen && (
        <AddUnitMemberModal open={addOpen} onOpenChange={setAddOpen} orgId={orgId} groupId={group.id}
          existingIds={new Set(members as string[])} onAdded={refreshMembers} orgUnitIds={orgUnitIds} />
      )}
      <DeleteGroupDialog open={deleteOpen} onOpenChange={setDeleteOpen} groupName={group.name}
        policyCount={refCount} onConfirm={() => deleteM.mutate()} isPending={deleteM.isPending} />
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

export function AccessControlPage() {
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [selectedOrg, setSelectedOrg] = useState<number | null>(null);
  const [newDriverName, setNewDriverName] = useState("");
  const [newChargerName, setNewChargerName] = useState("");
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AccessRule | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<AccessPolicy | null>(null);

  const orgsQ = useQuery({ queryKey: ["organizations"], queryFn: fetchOrganizations });

  // Auto-select org: for non-admin pick their only org; for admin pick first available
  useEffect(() => {
    if (selectedOrg) return;
    const orgs = orgsQ.data?.organizations ?? [];
    if (orgs.length > 0) setSelectedOrg(orgs[0].id);
  }, [orgsQ.data, selectedOrg]);

  const orgDetailQ = useQuery({
    queryKey: ["organization", selectedOrg],
    queryFn: () => fetchOrganization(selectedOrg!),
    enabled: !!selectedOrg,
  });

  const userMap = useMemo(() => {
    const users = orgDetailQ.data?.organization?.users ?? [];
    return new Map(users.map((u) => [u.id, u.username]));
  }, [orgDetailQ.data]);

  const driverGroupsQ = useQuery({ queryKey: ["driver-groups", selectedOrg], queryFn: () => fetchDriverGroups(selectedOrg!), enabled: !!selectedOrg });
  const chargerGroupsQ = useQuery({ queryKey: ["charger-groups", selectedOrg], queryFn: () => fetchChargerGroups(selectedOrg!), enabled: !!selectedOrg });
  const rulesQ = useQuery({ queryKey: ["access-rules", selectedOrg], queryFn: () => fetchAccessRules(selectedOrg!), enabled: !!selectedOrg });
  // Fetch all policies (including managed) for group reference counting
  const allPoliciesQ = useQuery({ queryKey: ["access-policies-all", selectedOrg], queryFn: () => fetchAccessPolicies(selectedOrg!, true), enabled: !!selectedOrg });
  // Fetch only manual (non-managed) policies for the advanced section
  const manualPoliciesQ = useQuery({ queryKey: ["access-policies-manual", selectedOrg], queryFn: () => fetchAccessPolicies(selectedOrg!), enabled: !!selectedOrg });

  const createDriverM = useMutation({
    mutationFn: () => {
      if (isReservedManualGroupName(newDriverName)) return Promise.reject(new Error(RESERVED_GROUP_NAME_MESSAGE));
      return createDriverGroup(selectedOrg!, newDriverName.trim());
    },
    onSuccess: () => { toast.success("Driver group created"); setNewDriverName(""); driverGroupsQ.refetch(); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const createChargerM = useMutation({
    mutationFn: () => {
      if (isReservedManualGroupName(newChargerName)) return Promise.reject(new Error(RESERVED_GROUP_NAME_MESSAGE));
      return createChargerGroup(selectedOrg!, newChargerName.trim());
    },
    onSuccess: () => { toast.success("Charger group created"); setNewChargerName(""); chargerGroupsQ.refetch(); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const toggleRuleM = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) => updateAccessRule(selectedOrg!, id, { enabled }),
    onSuccess: () => { rulesQ.refetch(); allPoliciesQ.refetch(); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const deleteRuleM = useMutation({
    mutationFn: (ruleId: number) => deleteAccessRule(selectedOrg!, ruleId),
    onSuccess: () => { toast.success("Rule deleted"); rulesQ.refetch(); allPoliciesQ.refetch(); chargerGroupsQ.refetch(); driverGroupsQ.refetch(); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const togglePolicyM = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) => updateAccessPolicy(selectedOrg!, id, { enabled }),
    onSuccess: () => { manualPoliciesQ.refetch(); allPoliciesQ.refetch(); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const deletePolicyM = useMutation({
    mutationFn: async (policy: AccessPolicy) => {
      await deleteAccessPolicy(selectedOrg!, policy.id);
      const cg = chargerGroups.find((g) => g.id === policy.charger_group_id);
      if (cg && isAutoGroup(cg.name)) {
        const allPols = allPolicies;
        const otherRefs = allPols.filter((p) => p.id !== policy.id && p.charger_group_id === cg.id).length;
        if (otherRefs === 0) {
          await deleteChargerGroup(selectedOrg!, cg.id).catch(() => {});
        }
      }
    },
    onSuccess: () => { toast.success("Policy deleted"); manualPoliciesQ.refetch(); allPoliciesQ.refetch(); chargerGroupsQ.refetch(); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const allDriverGroups = driverGroupsQ.data?.driver_groups ?? [];
  const allChargerGroups = chargerGroupsQ.data?.charger_groups ?? [];
  const driverGroups = allDriverGroups;
  const chargerGroups = allChargerGroups;
  const visibleDriverGroups = allDriverGroups.filter((g) => !isAutoGroup(g.name) && !g.is_builtin);
  const visibleChargerGroups = allChargerGroups.filter((g) => !isAutoGroup(g.name));
  const rules = rulesQ.data?.access_rules ?? [];
  const allPolicies = allPoliciesQ.data?.access_policies ?? [];
  const manualPolicies = manualPoliciesQ.data?.access_policies ?? [];

  const driverGroupMap = useMemo(() => new Map(driverGroups.map((g) => [g.id, g.name])), [driverGroups]);
  const chargerGroupMap = useMemo(() => new Map(chargerGroups.map((g) => [g.id, g.name])), [chargerGroups]);

  const refreshAll = () => { rulesQ.refetch(); allPoliciesQ.refetch(); manualPoliciesQ.refetch(); chargerGroupsQ.refetch(); driverGroupsQ.refetch(); };

  // Rules table columns
  const ruleColumns = useMemo<ColumnDef<AccessRule, any>[]>(
    () => [
      {
        accessorKey: "rule_type",
        header: "Type",
        cell: ({ getValue }) => {
          const v = getValue() as string;
          const meta = RULE_TYPE_LABELS[v];
          return <Badge variant={meta?.variant ?? "neutral"} className="badge-sm">{meta?.label ?? v}</Badge>;
        },
      },
      {
        accessorKey: "name",
        header: "Rule Name",
        cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>,
      },
      {
        accessorKey: "driver_group_id",
        header: "Who",
        cell: ({ getValue }) => <GroupRefCell name={driverGroupMap.get(getValue() as number)} />,
      },
      {
        accessorKey: "charger_group_id",
        header: "Where",
        cell: ({ getValue }) => <GroupRefCell name={chargerGroupMap.get(getValue() as number)} />,
      },
      {
        accessorKey: "enabled",
        header: "Status",
        cell: ({ row }) => (
          <Button size="xs"
            variant={row.original.enabled ? "default" : "outline"}
            onClick={(e) => { e.stopPropagation(); toggleRuleM.mutate({ id: row.original.id, enabled: !row.original.enabled }); }}
          >
            {row.original.enabled ? "ON" : "OFF"}
          </Button>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex gap-1">
            <Button size="xs" variant="outline"
              onClick={(e) => { e.stopPropagation(); setEditingRule(row.original); }}>
              Edit
            </Button>
            <Button size="icon-xs" variant="ghost" className="text-destructive hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`Delete rule "${row.original.name}" and its managed policies?`)) {
                  deleteRuleM.mutate(row.original.id);
                }
              }}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ),
      },
    ],
    [driverGroupMap, chargerGroupMap],
  );

  // Manual policies table columns
  const policyColumns = useMemo<ColumnDef<AccessPolicy, any>[]>(
    () => [
      {
        accessorKey: "priority",
        header: "Priority",
        cell: ({ getValue }) => <span className="font-mono text-xs">{getValue()}</span>,
      },
      {
        accessorKey: "rule_type",
        header: "Type",
        cell: ({ getValue }) => {
          const v = getValue() as string;
          return <Badge variant={v === "deny" ? "error" : "success"} className="badge-sm uppercase">{v}</Badge>;
        },
      },
      {
        accessorKey: "name",
        header: "Policy Name",
        cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>,
      },
      {
        accessorKey: "driver_group_id",
        header: "Who",
        cell: ({ getValue }) => <GroupRefCell name={driverGroupMap.get(getValue() as number)} />,
      },
      {
        accessorKey: "charger_group_id",
        header: "Where",
        cell: ({ getValue }) => <GroupRefCell name={chargerGroupMap.get(getValue() as number)} />,
      },
      {
        accessorKey: "enabled",
        header: "Status",
        cell: ({ row }) => (
          <Button size="xs"
            variant={row.original.enabled ? "default" : "outline"}
            onClick={(e) => { e.stopPropagation(); togglePolicyM.mutate({ id: row.original.id, enabled: !row.original.enabled }); }}
          >
            {row.original.enabled ? "ON" : "OFF"}
          </Button>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex gap-1">
            <Button size="xs" variant="outline"
              onClick={(e) => { e.stopPropagation(); setEditingPolicy(row.original); }}>
              Edit
            </Button>
            <Button size="icon-xs" variant="ghost" className="text-destructive hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`Delete policy "${row.original.name}"?`)) deletePolicyM.mutate(row.original);
              }}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ),
      },
    ],
    [driverGroupMap, chargerGroupMap, chargerGroups, allPolicies],
  );

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Access Control</h1>
          <div className="text-sm opacity-70">Manage who can charge on which chargers within organizations</div>
        </div>
        {isAdmin && (
          <OrgSelector orgs={orgsQ.data?.organizations ?? []} selected={selectedOrg} onSelect={setSelectedOrg} />
        )}
      </div>

      {!selectedOrg ? (
        <Card>
          <CardContent className="py-12 text-center text-sm opacity-50">
            Select an organization above to manage access control rules.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Groups side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="space-y-3">
              <h2 className="text-lg font-semibold">Driver Groups</h2>
              <div className="flex gap-2">
                <Input placeholder="New group name..." value={newDriverName} onChange={(e) => setNewDriverName(e.target.value)} className="flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter" && newDriverName.trim() && !isReservedManualGroupName(newDriverName)) createDriverM.mutate(); }} />
                <Button onClick={() => createDriverM.mutate()} disabled={createDriverM.isPending || !newDriverName.trim() || isReservedManualGroupName(newDriverName)}>Create</Button>
              </div>
              {isReservedManualGroupName(newDriverName) && (
                <div className="text-xs text-destructive">{RESERVED_GROUP_NAME_MESSAGE}</div>
              )}
              {driverGroupsQ.isLoading ? <div className="text-sm opacity-50">Loading...</div>
                : visibleDriverGroups.length === 0 ? <div className="text-sm opacity-50 italic">No driver groups</div>
                : <div className="space-y-3">
                    {visibleDriverGroups.map((g) => (
                      <GroupCard key={g.id} group={g} type="driver" orgId={selectedOrg} policies={allPolicies}
                        onDeleted={refreshAll} userMap={userMap} />
                    ))}
                  </div>}
            </div>

            <div className="space-y-3">
              <h2 className="text-lg font-semibold">Charger Groups</h2>
              <div className="flex gap-2">
                <Input placeholder="New group name..." value={newChargerName} onChange={(e) => setNewChargerName(e.target.value)} className="flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter" && newChargerName.trim() && !isReservedManualGroupName(newChargerName)) createChargerM.mutate(); }} />
                <Button onClick={() => createChargerM.mutate()} disabled={createChargerM.isPending || !newChargerName.trim() || isReservedManualGroupName(newChargerName)}>Create</Button>
              </div>
              {isReservedManualGroupName(newChargerName) && (
                <div className="text-xs text-destructive">{RESERVED_GROUP_NAME_MESSAGE}</div>
              )}
              {chargerGroupsQ.isLoading ? <div className="text-sm opacity-50">Loading...</div>
                : visibleChargerGroups.length === 0 ? <div className="text-sm opacity-50 italic">No charger groups</div>
                : <div className="space-y-3">
                    {visibleChargerGroups.map((g) => (
                      <GroupCard key={g.id} group={g} type="charger" orgId={selectedOrg} policies={allPolicies}
                        onDeleted={refreshAll} orgUnitIds={orgDetailQ.data?.organization?.unit_ids} />
                    ))}
                  </div>}
            </div>
          </div>

          {/* Access Rules (primary) */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Access Rules</CardTitle>
                  <div className="text-sm opacity-60 mt-1">
                    Define who can charge where. Rules automatically manage the underlying policies.
                  </div>
                </div>
                <Button onClick={() => { setEditingRule(null); setRuleModalOpen(true); }}
                  disabled={driverGroups.length === 0}>
                  + New Rule
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {driverGroups.length === 0 ? (
                <div className="text-sm opacity-50 italic py-4 text-center">
                  Create at least one driver group before adding rules.
                </div>
              ) : (
                <DataTable columns={ruleColumns} data={rules} isLoading={rulesQ.isLoading}
                  getRowId={(r) => String(r.id)}
                  emptyMessage="No access rules. All chargers are open by default." />
              )}
            </CardContent>
          </Card>

          {/* Advanced Policies (collapsed, only if manual policies exist) */}
          {manualPolicies.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between cursor-pointer" onClick={() => setAdvancedOpen(!advancedOpen)}>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      Advanced Policies
                      <Badge variant="neutral" className="badge-sm">{manualPolicies.length}</Badge>
                    </CardTitle>
                    <div className="text-sm opacity-60 mt-1">
                      Manually created DENY/GRANT policies with priority-based evaluation.
                    </div>
                  </div>
                  <Button size="sm" variant="ghost">{advancedOpen ? "Collapse" : "Expand"}</Button>
                </div>
              </CardHeader>
              {advancedOpen && (
                <CardContent>
                  <div className="flex justify-end mb-3">
                    <Button size="sm" variant="outline" onClick={() => { setEditingPolicy(null); setPolicyModalOpen(true); }}>
                      + New Advanced Policy
                    </Button>
                  </div>
                  <DataTable columns={policyColumns} data={manualPolicies} isLoading={manualPoliciesQ.isLoading}
                    getRowId={(p) => String(p.id)} />
                </CardContent>
              )}
            </Card>
          )}


          {/* Rule Create / Edit Modal */}
          <RuleFormModal
            open={ruleModalOpen || !!editingRule}
            onOpenChange={(v) => { if (!v) { setRuleModalOpen(false); setEditingRule(null); } }}
            orgId={selectedOrg}
            driverGroups={driverGroups}
            chargerGroups={chargerGroups}
            orgUsers={orgDetailQ.data?.organization?.users ?? []}
            onSaved={refreshAll}
            editRule={editingRule}
            orgUnitIds={orgDetailQ.data?.organization?.unit_ids}
          />

          {/* Legacy Policy Create / Edit Modal */}
          <PolicyFormModal
            open={policyModalOpen || !!editingPolicy}
            onOpenChange={(v) => { if (!v) { setPolicyModalOpen(false); setEditingPolicy(null); } }}
            orgId={selectedOrg}
            driverGroups={driverGroups}
            chargerGroups={chargerGroups}
            onSaved={refreshAll}
            editPolicy={editingPolicy}
            orgUnitIds={orgDetailQ.data?.organization?.unit_ids}
          />
        </>
      )}
    </div>
  );
}
